import Suspense from '@/Components/Suspense';
import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { useLatestValuesRef } from '@/Utility/hooks/useLatestValuesRef';
import {
  type Tag_Pagination_Fragment$key,
  TagsPaginationFragment,
  TagsQuery,
  type Tags_Query,
  useTagSearchQuery,
} from '@/graphql/queries/Tag.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { CircularProgress } from '@mui/material';
import { atom, useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';
import { usePreloadedQuery, useQueryLoader } from 'react-relay';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const tagsAtom = atom<Array<{ name: string; id: string }>>([]);

const tagsSearchSchema = z.object({
  tags: z.array(z.string()).nullish(),
});

const tagsComponentProps = z.object({
  displayName: z.string().optional(),
});

type TagsFilterProps = z.infer<typeof tagsComponentProps>;

type RelayData = ReturnType<typeof usePaginationFragment<Tags_Query, Tag_Pagination_Fragment$key>>;

type TagComponentProps = {
  displayName: string;
  isFallback?: boolean;
} & Partial<RelayData>;

const TagComponent = React.memo(({ displayName, isFallback, ...relayProps }: TagComponentProps) => {
  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(tagsSearchSchema);

  const updateTags = useCallback(
    (tags: string[] | undefined) => {
      updateFilter({
        tags: tags,
      });
    },
    [updateFilter]
  );

  const handleTagToggle = useCallback(
    (tagId?: string | null) => {
      if (!tagId) return;

      updateFilter((prev) => {
        let newTags = [...(prev?.tags ?? []), tagId];
        if (prev.tags?.includes(tagId)) {
          newTags = newTags.filter((id) => id !== tagId);
        }

        return {
          tags: newTags.length ? newTags : undefined,
        };
      });
    },
    [updateTags]
  );

  const relayTagNodes = useMemo(() => {
    if (!relayProps?.data?.tags?.edges) return [];

    return relayProps.data.tags.edges.map(({ node }) => ({
      name: node?.name ?? '',
      id: node?.id ?? '',
    }));
  }, [relayProps?.data?.tags]);

  const [tagsArray, setTagsArray] = useAtom(tagsAtom);
  const tagsArrayRef = useLatestValuesRef({ tags: tagsArray });
  const [__fetchTagSearchQuery, isLoadingTagSearchQuery] = useTagSearchQuery();

  useEffect(() => {
    setTagsArray((prev) => {
      relayTagNodes.forEach((tag) => {
        if (!prev.find((item) => item.id === tag.id)) {
          prev.push(tag);
        }
      });

      return [...prev];
    });
  }, [relayTagNodes]);

  const fetchTagSearchQuery = useCallback((args: Parameters<typeof __fetchTagSearchQuery>[0]) => {
    __fetchTagSearchQuery(
      {
        first: 24,
        where: args?.where,
      },
      {
        onComplete(data, errors) {
          if (!!errors?.length || !data?.tags?.edges?.length) {
            return;
          }

          const tagsRef = tagsArrayRef.current.tags;

          const newTags =
            data?.tags?.edges?.map(({ node }) => ({
              id: node?.id ?? '',
              name: node?.name ?? '',
            })) || [];

          if (!newTags.length) return;
          if (newTags.every((tag) => tagsRef.find((t) => t.id === tag.id))) return;

          setTagsArray((prev) => {
            data?.tags?.edges?.forEach(({ node }) => {
              if (!prev.find((item) => item.id === node?.id)) {
                prev.push({
                  id: node?.id ?? '',
                  name: node?.name ?? '',
                });
              }
            });

            return [...prev];
          });
        },
      }
    );
  }, []);

  useEffect(() => {
    if (isLoadingTagSearchQuery) return;

    if (!slowFilters?.tags?.length) return;

    // if all the tags are already loaded, do not fetch again
    if (slowFilters?.tags?.every((tag) => tagsArray?.some((t) => t.id === tag)) && !isFallback) {
      return;
    }

    fetchTagSearchQuery({ where: { include: slowFilters.tags } });
  }, [slowFilters]);

  const menuList: DropDownMenuItemProps[] = useMemo(() => {
    if (isFallback) {
      return [{ name: 'Loading...', icon: <CircularProgress size={25} />, initValue: false, multi: false }];
    }

    const items = [] as DropDownMenuItemProps[];

    // Handle empty tags list
    if (!tagsArray?.length) {
      items.push({
        name: 'No Tags Found',
        value: '___no-tags___',
      });

      return items;
    }

    tagsArray.forEach((tag) => {
      items.push({
        name: tag?.name ?? '',
        value: tag?.id,
        initValue: slowFilters?.tags?.includes(tag.id) || false,
        onClick: (_, ref) => {
          if (!tag?.id) return false;

          handleTagToggle(tag.id);
        },
      });
    });

    return items;
  }, [tagsArray, isFallback, handleTagToggle]);

  const handleSearch = useCallback(
    (search: string | null) => {
      fetchTagSearchQuery({
        where: {
          search,
          exclude: fastFilters?.tags,
        },
      });
    },
    [fetchTagSearchQuery, fastFilters?.tags]
  );

  const loadingProps = useLatestValuesRef({
    hasNext: relayProps?.hasNext,
    isLoadingNext: relayProps?.isLoadingNext,
    isRefetching: relayProps?.isRefetching,
    loadNext: relayProps?.loadNext,
  });

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className="z-btn-6"
        name={displayName ?? 'Tags'}
        menuList={menuList}
        type="multiple-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'tags',
          isLoading: relayProps?.isLoadingNext || relayProps?.isRefetching || isLoadingTagSearchQuery,
          isSearchable: true,
          onSearchChange: handleSearch,
          showCheckedOnTop: true,
          clearSearchOnClose: true,
          onScrollEnd: () => {
            const loading = loadingProps.current;
            if (loading?.hasNext && !loading?.isLoadingNext && !loading?.isRefetching) {
              loading?.loadNext?.(24);
            }
          },
        }}
      />
      <FilterBadge count={fastFilters.tags?.length || 0} isUpdating={isUpdating} />
    </div>
  );
});

type QueryLoaderProps = {
  queryRef: NonNullable<ReturnType<typeof useQueryLoader<Tags_Query>>['0']>;
  displayName: string;
};

const QueryLoader = React.memo(({ queryRef, displayName }: QueryLoaderProps) => {
  const data = usePreloadedQuery<Tags_Query>(TagsQuery, queryRef);

  const paginationData = usePaginationFragment<Tags_Query, Tag_Pagination_Fragment$key>(TagsPaginationFragment, data, {
    environment: RelayEnvironment,
    usePageLoading: false,
  });

  return <TagComponent displayName={displayName} {...paginationData} />;
});

const TagsFilter = ({ displayName = 'Tags' }: TagsFilterProps) => {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<Tags_Query>(TagsQuery);

  useEffect(() => {
    loadQuery(
      {
        first: 24,
      },
      { fetchPolicy: 'store-or-network' }
    );
    return () => {
      disposeQuery();
    };
  }, [loadQuery, disposeQuery]);

  return (
    <Suspense fallback={<TagComponent displayName={displayName} isFallback />}>
      {queryRef && <QueryLoader queryRef={queryRef} displayName={displayName} />}
    </Suspense>
  );
};

const TagsFilterMemo = React.memo(TagsFilter);

export const FilterConfig_Tags = {
  tags: {
    searchSchema: tagsSearchSchema,
    component: TagsFilterMemo,
    componentProps: tagsComponentProps,
  },
} as const;
